/**
 * youtubeOAuthLauncher — browser/Electron-aware helper for opening the Google
 * OAuth consent URL without navigating away from the app, and for polling the
 * callback result.
 */

import { pollOAuthStatus, startOAuth, startWebOAuth } from './youtubePublishApi';
import type { OAuthStatusResponse } from './youtubePublishApi';
import { isElectronMode } from '../utils/apiMode';

export type OAuthMode = 'desktop' | 'web';

export function resolveOAuthMode(): OAuthMode {
  return isElectronMode() ? 'desktop' : 'web';
}

/**
 * Initiate the OAuth flow appropriate for the current runtime.
 *
 * Desktop (Electron): popup + loopback + polling. Returns the final status.
 * Web (browser): full-page redirect to Google. Never resolves — the caller is
 * navigated away. /oauth/callback handles the second half of the flow.
 */
export async function startOAuthFlow(): Promise<OAuthStatusResponse | void> {
  if (resolveOAuthMode() === 'desktop') {
    const { auth_url, state } = await startOAuth();
    openOAuthUrl(auth_url);
    return waitForOAuthCompletion(state);
  }

  const redirectUri = `${window.location.origin}/oauth/callback`;
  const { auth_url, state } = await startWebOAuth(redirectUri);
  sessionStorage.setItem('yt_oauth_state', state);
  sessionStorage.setItem('yt_oauth_return', window.location.pathname);
  window.location.assign(auth_url);
  return new Promise(() => undefined);
}

/**
 * Open the OAuth consent URL in an external window/tab.
 *
 * - Electron: use window.electronAPI.openExternal if available; fall back to
 *   window.open so the app never navigates away.
 * - Browser: open a small popup window so the user can complete the flow while
 *   keeping the app state intact.
 */
export function openOAuthUrl(url: string): void {
  // Electron exposes openExternal via the preload bridge.
  const electronApi = (window as unknown as Record<string, unknown>).electronAPI as
    | { openExternal?: (url: string) => void }
    | undefined;

  if (typeof electronApi?.openExternal === 'function') {
    electronApi.openExternal(url);
    return;
  }

  // Browser: open popup so we keep the SPA state.
  window.open(url, 'youtube-oauth', 'width=600,height=700,noopener');
}

/**
 * Poll the backend until the OAuth flow completes or fails.
 *
 * Uses recursive setTimeout (not setInterval) to avoid overlapping requests.
 *
 * @param state       The `state` token returned by `startOAuth()`.
 * @param onProgress  Optional callback invoked on each pending tick.
 * @param timeoutMs   Max time to wait in ms (default 10 min).
 * @returns           Resolved OAuthStatusResponse when status != "pending".
 */
export function waitForOAuthCompletion(
  state: string,
  onProgress?: (tick: number) => void,
  timeoutMs = 600_000
): Promise<OAuthStatusResponse> {
  return new Promise<OAuthStatusResponse>((resolve, reject) => {
    const started = Date.now();
    let tick = 0;

    const poll = (): void => {
      if (Date.now() - started > timeoutMs) {
        reject(new Error('YouTube OAuth timed out — please try again.'));
        return;
      }

      pollOAuthStatus(state)
        .then((result) => {
          if (result.status !== 'pending') {
            resolve(result);
            return;
          }
          tick += 1;
          onProgress?.(tick);
          // Schedule next poll — 2 s delay, no overlap possible.
          setTimeout(poll, 2_000);
        })
        .catch((err: unknown) => {
          reject(err instanceof Error ? err : new Error(String(err)));
        });
    };

    // First poll after a short delay so the browser window has a moment to open.
    setTimeout(poll, 2_000);
  });
}
